import React from 'react';
import { Platform, View } from 'react-native';
import styled from 'styled-components/native';
import { P, B } from '../Typography';

const DATA_POINT_SPACE = 16;
const DATA_POINT_SIZE = 17;

const StyledChartView = styled.ScrollView`
  width: 100%;
  background-color: white;
  margin-top: 26px;
`;

const StyledVerticalLine = styled.View<{
  transparent?: boolean;
  isEnd?: boolean;
}>`
  width: ${p => (p.isEnd ? 1 : 0.5)}px;
  flex: 1;
  background-color: ${p => (p.transparent ? 'transparent' : '#494753')};
  opacity: ${p => (p.isEnd ? 1 : 0.62)};
`;

const StyledHorizontalLine = styled.View<{ isFirstDataSet: boolean }>`
  width: ${DATA_POINT_SPACE}px;
  height: 1px;
  background-color: #494753;
  ${p => (p.isFirstDataSet ? 'margin-top: 18px;' : '')}// 18px line-height of P
`;

const StyledLabelView = styled.View`
  background-color: white;
  width: 100px;
  margin: 4px 8px 4px 0px;
  min-height: 36px;
  justify-content: center;
`;

const StyledDataPoint = styled.View<{
  done: boolean | undefined;
  past?: boolean;
}>`
  width: ${DATA_POINT_SIZE}px;
  height: ${DATA_POINT_SIZE}px;
  border-radius: 12.5px;
  background-color: ${p =>
    p.past
      ? '#DFD7D7'
      : p.done
        ? '#1c9c7d'
        : p.done === false
          ? '#494753'
          : 'white'};
  border: 1px solid #494753;
`;

const StyledBlankDataPoint = styled.View<{ transparent?: boolean }>`
  width: ${DATA_POINT_SIZE / 2}px;
  height: 1px;
  background-color: ${p => (p.transparent ? 'transparent' : '#494753')};
`;

const StyledBlankDataPointView = styled.View`
  flex-direction: row;
`;

const StyledDataPointRowView = styled.View`
  flex-direction: row;
  width: 100%;
  align-items: center;
`;

const StyledDataPointVerticalView = styled.View`
  align-items: center;
  height: 100%;
  width: ${DATA_POINT_SIZE}px;
  overflow: visible;
`;

const StyledDataPointView = styled.View`
  width: ${Platform.OS === 'web' ? 0 : '100%'};
  height: 100%;
`;

const StyledP = styled(P)`
  text-align: center;
`;

const StyledDayLabelContainer = styled.View`
  align-items: center;
`;

const StyledCurrentDayBorder = styled.View`
  height: 2.67px;
  width: 17.87px;
  border-radius: 8.2px;
  top: 18px;
  position: absolute;
  background-color: black;
`;

const StyledDayView = styled.View`
  width: ${DATA_POINT_SIZE + DATA_POINT_SPACE}px;
  align-items: center;
`;

const StyledEventName = styled(P)`
  text-align: right;
`;

const StyledDayTitle = styled(B)`
  margin-bottom: 16px;
  text-align: right;
  padding-right: 8px;
`;

const StyledContainer = styled.View``;

export type EventObject = {
  name: string;
  done?: boolean;
};

export interface DiaryEvent {
  events: {
    day: number;
    done: boolean | undefined;
    eventIdentifier?: {
      intendedStartDate: number;
      studyTemplateEventId: number;
      repeatCount: number;
    };
  }[];
  name: string;
}

export interface DataDiaryProps {
  currentDay: number;
  diaryEvents: DiaryEvent[];
  studyLength: number;
}

// Merge same name event's data points
const mergeEvents = function (data: DiaryEvent[]) {
  const result: DiaryEvent[] = [];
  data.forEach(event => {
    const existingObj = result.find(e => e.name === event.name);
    if (existingObj) {
      event.events.forEach(item => {
        const existingItem = existingObj.events.find(e => e.day === item.day);
        if (existingItem) {
          existingItem.done = existingItem.done
            ? existingItem.done
            : item.done
              ? item.done
              : undefined;
        } else {
          existingObj.events.push(item);
        }
      });
    } else {
      result.push(event);
    }
  });
  return result;
};

const DataDiary: React.FC<DataDiaryProps> = ({
  currentDay,
  diaryEvents,
  studyLength
}) => {
  const mergedEvents = mergeEvents(mergeEvents(diaryEvents));
  const buildHorizontalData = (
    diaryEvent: DiaryEvent,
    studyLength: number,
    isFirstDataSet: boolean,
    isLastDataSet: boolean
  ) => {
    const dataRow: JSX.Element[] = [];
    let previousDay = 0;

    // creates horizontal line
    const blankDataPointView = (isLast?: boolean, isFirst?: boolean) => (
      <StyledDataPointVerticalView
        key={`blank-${diaryEvent.name}-${previousDay}`}
      >
        {isFirstDataSet && (
          <StyledDayView>
            <StyledP color='dark'>{previousDay}</StyledP>
            {currentDay === previousDay && <StyledCurrentDayBorder />}
          </StyledDayView>
        )}
        <StyledVerticalLine
          transparent={isFirstDataSet}
          isEnd={isLast || isFirst}
        />
        <StyledBlankDataPointView>
          <StyledBlankDataPoint transparent={isFirst} />
          <StyledBlankDataPoint transparent={isLast} />
        </StyledBlankDataPointView>
        <StyledVerticalLine
          transparent={isLastDataSet}
          isEnd={isLast || isFirst}
        />
      </StyledDataPointVerticalView>
    );

    // create circle data point
    const dataPointView = (
      isLast?: boolean,
      isFirst?: boolean,
      done?: boolean
    ) => (
      <StyledDataPointVerticalView
        key={`data-point-${diaryEvent.name}-${previousDay}`}
      >
        {isFirstDataSet && (
          <StyledDayView>
            <StyledDayLabelContainer>
              <StyledP color='dark'>{previousDay}</StyledP>
              {currentDay === previousDay && <StyledCurrentDayBorder />}
            </StyledDayLabelContainer>
          </StyledDayView>
        )}
        <StyledVerticalLine
          transparent={isFirstDataSet}
          isEnd={isLast || isFirst}
        />
        <StyledDataPoint
          done={done}
          past={currentDay ? previousDay > currentDay : false}
        />
        <StyledVerticalLine
          transparent={isLastDataSet}
          isEnd={isLast || isFirst}
        />
      </StyledDataPointVerticalView>
    );

    dataRow.push(
      <View key={`label-${diaryEvent.name}`}>
        {isFirstDataSet && <StyledDayTitle color='dark'>DAY</StyledDayTitle>}
        <StyledLabelView>
          <StyledEventName color='dark'>{diaryEvent.name}</StyledEventName>
        </StyledLabelView>
      </View>
    );

    // Add Data points
    diaryEvent.events
      .sort((a, b) => a.day - b.day)
      .forEach(data => {
        while (previousDay < data.day) {
          previousDay++;
          if (previousDay === data.day) {
            dataRow.push(
              dataPointView(
                previousDay === studyLength,
                previousDay === 1,
                data.done
              )
            );
          } else {
            dataRow.push(
              blankDataPointView(previousDay === studyLength, previousDay === 1)
            );
          }
          if (previousDay !== studyLength) {
            dataRow.push(
              <StyledHorizontalLine
                key={`horizontal-${diaryEvent.name}-${previousDay}`}
                isFirstDataSet={isFirstDataSet}
              />
            );
          }
        }
      });

    // Fill the rest with horizontal lines
    if (previousDay !== studyLength) {
      previousDay++;
      for (previousDay; previousDay <= studyLength; previousDay++) {
        dataRow.push(
          blankDataPointView(previousDay === studyLength, previousDay === 1)
        );
        if (previousDay !== studyLength) {
          dataRow.push(
            <StyledHorizontalLine
              key={`fill-${diaryEvent.name}-${previousDay}`}
              isFirstDataSet={isFirstDataSet}
            />
          );
        }
      }
    }
    return dataRow;
  };

  return (
    <StyledContainer>
      <StyledChartView horizontal removeClippedSubviews={false}>
        <StyledDataPointView>
          {mergedEvents.map((record, index) => {
            return (
              <StyledDataPointRowView
                key={`data-diary-${record.name}-${index}`}
              >
                {buildHorizontalData(
                  record,
                  studyLength,
                  index === 0,
                  index + 1 === mergedEvents.length
                )}
              </StyledDataPointRowView>
            );
          })}
        </StyledDataPointView>
      </StyledChartView>
    </StyledContainer>
  );
};

export default DataDiary;
